import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChefHat, Check, ArrowRight, QrCode, BarChart3, Boxes, Users,
  CalendarClock, CreditCard, Sparkles, Store, Menu as MenuIcon, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO } from "@/lib/brand";

const STARTER = [
  "Online menu management", "Business profile page", "QR code menu",
  "Inventory tracking", "Daily sales reports", "Employee scheduling",
  "Clock in / Clock out", "Customer database", "1–3 staff accounts",
  "Online ordering system", "Digital gift cards",
];
const PRO = [
  "Everything in Starter", "Advanced inventory & food cost tracking",
  "Recipe management", "Table reservations", "Payroll export",
  "Customer loyalty program", "Email & SMS marketing", "Performance dashboards",
  "3–10 staff accounts", "POS integration", "Multi-location management",
  "AI sales & inventory forecasting", "Kitchen display integration",
  "Custom reporting", "Franchise dashboard", "Unlimited locations",
];

const FEATURES = [
  { icon: QrCode, title: "QR Code Menus", desc: "Contactless menus your guests scan and order from instantly.", span: "md:col-span-5" },
  { icon: BarChart3, title: "Daily Sales Reports", desc: "Know your numbers every morning with clear revenue dashboards.", span: "md:col-span-7" },
  { icon: Boxes, title: "Smart Inventory", desc: "Track stock, get low-stock alerts, and cut food waste.", span: "md:col-span-7" },
  { icon: Sparkles, title: "AI Forecasting", desc: "Predict sales and inventory needs before you run short.", span: "md:col-span-5" },
  { icon: CalendarClock, title: "Staff & Time Clock", desc: "Schedule shifts and let staff clock in and out with ease.", span: "md:col-span-4" },
  { icon: Users, title: "Customer Database", desc: "Build loyalty with guest profiles and reward points.", span: "md:col-span-4" },
  { icon: Store, title: "Multi-location", desc: "Run one café or fifty from a single franchise dashboard.", span: "md:col-span-4" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <img src={LOGO} alt="manage.kitchen" className="h-11 w-auto object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <a href="#features" className="hover:text-foreground transition-colors">Features</a>
          <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login"><Button data-testid="nav-login-btn" variant="ghost" className="hover:bg-muted">Log in</Button></Link>
          <Link to="/register"><Button data-testid="nav-signup-btn" className="bg-primary hover:bg-[#B35A42] text-primary-foreground transition-all hover:-translate-y-px">Start free trial</Button></Link>
        </div>
        <button data-testid="mobile-menu-btn" className="md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X /> : <MenuIcon />}
        </button>
      </div>
      {open && (
        <div className="md:hidden px-6 pb-4 flex flex-col gap-3 border-t border-border pt-3">
          <a href="#features" onClick={() => setOpen(false)}>Features</a>
          <a href="#pricing" onClick={() => setOpen(false)}>Pricing</a>
          <Link to="/login"><Button variant="outline" className="w-full">Log in</Button></Link>
          <Link to="/register"><Button className="w-full bg-primary text-primary-foreground">Start free trial</Button></Link>
        </div>
      )}
    </header>
  );
};

function PlanCard({ name, price, tagline, features, featured, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl p-8 flex flex-col h-full ${
        featured
          ? "bg-foreground text-[#FDFBF7] shadow-[0_24px_60px_rgba(26,26,26,0.25)]"
          : "bg-card shadow-[0_8px_32px_rgba(200,106,81,0.06)]"
      }`}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">{name}</h3>
        {featured && <span className="text-xs uppercase tracking-[0.2em] bg-primary px-3 py-1 rounded-full">Popular</span>}
      </div>
      <p className={`mt-2 text-sm ${featured ? "text-[#FDFBF7]/70" : "text-muted-foreground"}`}>{tagline}</p>
      <div className="mt-6 flex items-end gap-1">
        <span className="font-heading text-5xl font-semibold">${price}</span>
        <span className={`mb-2 text-sm ${featured ? "text-[#FDFBF7]/70" : "text-muted-foreground"}`}>/month</span>
      </div>
      <Link to="/register" className="mt-6">
        <Button
          data-testid={`${testid}-cta`}
          className={`w-full transition-all hover:-translate-y-px ${
            featured ? "bg-primary hover:bg-[#B35A42] text-primary-foreground" : "bg-foreground hover:bg-foreground/90 text-[#FDFBF7]"
          }`}
        >
          Start 14-day free trial <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </Link>
      <ul className="mt-8 space-y-3 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-accent" : "text-secondary"}`} strokeWidth={2} />
            <span className={featured ? "text-[#FDFBF7]/90" : "text-foreground/80"}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Nav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-secondary bg-muted px-3 py-1.5 rounded-full">
            <Sparkles className="h-3.5 w-3.5" /> Restaurant OS
          </span>
          <h1 className="mt-6 font-heading text-5xl sm:text-6xl font-medium tracking-tighter leading-[1.05]">
            Run your restaurant from one beautiful place.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
            Menus, inventory, staff, sales and AI forecasting — manage.kitchen brings every part
            of your café, food truck or fine-dining kitchen together. Free for 14 days.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button data-testid="hero-cta" onClick={() => navigate("/register")} size="lg"
              className="bg-primary hover:bg-[#B35A42] text-primary-foreground transition-all hover:-translate-y-px h-12 px-7">
              Start free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <a href="#pricing">
              <Button variant="outline" size="lg" className="h-12 px-7 border-border hover:bg-muted">See pricing</Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">No credit card required · Cancel anytime</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="relative"
        >
          <div className="rounded-2xl overflow-hidden shadow-[0_24px_60px_rgba(26,26,26,0.18)]">
            <img
              src="https://images.pexels.com/photos/17086288/pexels-photo-17086288.jpeg"
              alt="Chefs working in a modern restaurant kitchen"
              className="w-full h-[520px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-card rounded-xl p-5 shadow-[0_12px_40px_rgba(26,26,26,0.12)] hidden sm:block">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Today's revenue</p>
            <p className="font-heading text-3xl font-semibold mt-1">$2,184</p>
            <p className="text-sm text-secondary font-medium">▲ 12% vs last week</p>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">Everything your kitchen needs</h2>
          <p className="mt-4 text-muted-foreground text-lg">One platform replaces a dozen disconnected tools.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-fr">
          {FEATURES.map((f) => (
            <div key={f.title} className={`${f.span} bg-card rounded-2xl p-7 shadow-[0_8px_32px_rgba(200,106,81,0.05)] hover:-translate-y-0.5 transition-transform`}>
              <span className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
                <f.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </span>
              <h3 className="mt-5 text-xl font-medium">{f.title}</h3>
              <p className="mt-2 text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">Simple, honest pricing</h2>
          <p className="mt-4 text-muted-foreground text-lg">Start with a 14-day free trial. Upgrade when you're ready.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-8 items-stretch">
          <PlanCard name="Starter" price="99" tagline="Best for small restaurants, cafes & food trucks"
            features={STARTER} testid="pricing-starter" />
          <PlanCard name="Professional" price="249" tagline="Best for busy restaurants with multiple staff"
            features={PRO} featured testid="pricing-professional" />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative rounded-3xl overflow-hidden bg-foreground text-[#FDFBF7] p-12 sm:p-16">
          <img src="https://images.pexels.com/photos/11927241/pexels-photo-11927241.jpeg" alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-multiply" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">Ready to serve smarter?</h2>
            <p className="mt-4 text-[#FDFBF7]/70 text-lg">Join restaurants running calmer, more profitable kitchens with manage.kitchen.</p>
            <Link to="/register">
              <Button data-testid="cta-bottom-btn" size="lg" className="mt-8 bg-primary hover:bg-[#B35A42] text-primary-foreground h-12 px-7 transition-all hover:-translate-y-px">
                Start your free trial <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="manage.kitchen" className="h-9 w-auto object-contain" />
          </div>
          <p>© 2026 manage.kitchen — Restaurant Management Platform. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
